"use client";

import { useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { Heart, Bed, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getMediumUrl } from "@/lib/utils/image-url";
import type { Listing } from "@/types";

interface ListingCardProps {
  listing: Listing;
  className?: string;
  showFavorite?: boolean;
  isFavorite?: boolean;
  onFavoriteClick?: (listingId: string) => void;
}

/**
 * Wander tarzı ilan kartı
 * project.md: Mülkün kapak görseli (sağ üst köşesinde favori için "kalp ikonu"), adı, konumu ve minimum fiyatı
 */
export function ListingCard({
  listing,
  className,
  showFavorite = true,
  isFavorite = false,
  onFavoriteClick,
}: ListingCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // İlk 3 görseli al
  const displayImages =
    listing.images && listing.images.length > 0 ? listing.images.slice(0, 3) : [listing.coverImage];

  const minSwipeDistance = 50;

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFavoriteClick?.(listing.id);
  };

  const handlePrevImage = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    },
    [displayImages.length]
  );

  const handleNextImage = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
    },
    [displayImages.length]
  );

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentImageIndex < displayImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
    if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  // En düşük fiyatı hesapla (base fiyat veya tarih bazlı fiyatlardan)
  const basePrice = listing.priceInfo.nightlyPrice;
  const pricingRulesPrices = listing.priceInfo.pricingRules?.map((rule) => rule.price) || [];
  const lowestPrice = Math.min(basePrice, ...pricingRulesPrices);
  const hasMultiplePrices = pricingRulesPrices.length > 0 && lowestPrice < basePrice;

  // Fiyat formatla
  const formattedPrice = new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: listing.priceInfo.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(lowestPrice);

  return (
    // "Stretched link" deseni: kapsayıcı <Link> KALDIRILDI.
    // HTML spesifikasyonunda <a> içeriği interaktif içerik barındıramaz; burada
    // <a> içinde ÜÇ <button> vardı (favori + iki ok). Tarayıcılar bunu kendi
    // kurtarma algoritmalarıyla toparlıyor, ekran okuyucularsa iç içe interaktif
    // yapıyı tutarsız biçimde okuyor ve klavye sırası öngörülemez oluyordu.
    // PR #28 kalbi gerçekten çalışır hale getirdiği için geçersiz iç içe kontrol
    // artık gerçek bir eylem taşıyordu.
    //
    // Yeni yapı: kart bir <article>, yalnız BAŞLIK bir <a> ve o <a> kendi
    // ::after'ıyla tüm kartı kaplıyor. Tıklanabilir alan AYNI kalıyor; butonlar
    // `z-10` ile kaplamanın üstünde durduğu için tıklamaları da aynı çalışıyor.
    <article className={cn("group relative space-y-3", className)}>
      {/* Görsel Carousel */}
      <div
        className="bg-muted relative aspect-[4/3] overflow-hidden rounded-xl"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Görseller */}
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
        >
          {displayImages.map((img, i) => (
            <div key={i} className="relative h-full w-full shrink-0">
              <Image
                src={getMediumUrl(img) || "/placeholder-listing.jpg"}
                alt={`${listing.title} - ${i + 1}`}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 25vw"
              />
            </div>
          ))}
        </div>

        {/* Sağ üst - Favori (Wander tarzı) */}
        {showFavorite && (
          <button
            type="button"
            onClick={handleFavoriteClick}
            className="absolute top-3 right-3 z-10 transition-transform hover:scale-110"
            aria-label={isFavorite ? "Favorilerden çıkar" : "Favorilere ekle"}
            aria-pressed={isFavorite}
          >
            <Heart
              className={cn(
                "h-6 w-6 drop-shadow-md transition-colors",
                isFavorite
                  ? "fill-red-500 text-red-500"
                  : "fill-white/30 text-white hover:fill-white/50"
              )}
            />
          </button>
        )}

        {/* Navigation arrows (hover'da görünür) */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className={cn(
                "absolute top-1/2 left-2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-1.5 shadow-md transition-all hover:bg-white",
                "opacity-0 group-hover:opacity-100",
                currentImageIndex === 0 && "invisible"
              )}
              aria-label="Önceki görsel"
            >
              <ChevronLeft className="h-4 w-4 text-gray-800" />
            </button>
            <button
              onClick={handleNextImage}
              className={cn(
                "absolute top-1/2 right-3 z-10 -translate-y-1/2 rounded-full bg-white/90 p-1.5 shadow-md transition-all hover:bg-white",
                "opacity-0 group-hover:opacity-100",
                currentImageIndex === displayImages.length - 1 && "invisible"
              )}
              aria-label="Sonraki görsel"
            >
              <ChevronRight className="h-4 w-4 text-gray-800" />
            </button>
          </>
        )}

        {/* Dots indicator */}
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
          {displayImages.map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-1.5 w-1.5 rounded-full transition-colors",
                i === currentImageIndex ? "bg-white" : "bg-white/50"
              )}
            />
          ))}
        </div>
      </div>

      {/* İçerik */}
      <div className="space-y-0.5">
        <p className="text-muted-foreground text-[10px] font-medium tracking-wider uppercase sm:text-[11px]">
          {listing.location.district}, {listing.location.city}
        </p>

        <h3 className="text-foreground group-hover:text-primary line-clamp-1 text-sm font-semibold sm:text-base">
          <Link
            href={`/listings/${listing.slug}`}
            target="_blank"
            className="after:absolute after:inset-0 after:content-['']"
          >
            {listing.title}
          </Link>
        </h3>

        <div className="text-muted-foreground flex items-center gap-2 text-xs sm:gap-3 sm:text-sm">
          <span className="flex items-center gap-1">
            <Bed className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            {listing.bedrooms}
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            {listing.capacity}
          </span>
        </div>

        <p className="pt-0.5">
          {hasMultiplePrices ? (
            <>
              <span className="text-foreground text-sm font-semibold sm:text-base">
                {formattedPrice}
              </span>
              <span className="text-muted-foreground text-xs sm:text-sm">&apos;den</span>
            </>
          ) : (
            <>
              <span className="text-foreground text-sm font-semibold sm:text-base">
                {formattedPrice}
              </span>
              <span className="text-muted-foreground text-xs sm:text-sm"> / gece</span>
            </>
          )}
        </p>
      </div>
    </article>
  );
}

// Skeleton versiyonu
export function ListingCardSkeleton() {
  return (
    <div className="space-y-3">
      <div className="bg-muted aspect-[4/3] animate-pulse rounded-xl" />
      <div className="space-y-2">
        <div className="bg-muted h-3 w-24 animate-pulse rounded" />
        <div className="bg-muted h-5 w-3/4 animate-pulse rounded" />
        <div className="bg-muted h-4 w-1/3 animate-pulse rounded" />
        <div className="bg-muted h-5 w-1/4 animate-pulse rounded" />
      </div>
    </div>
  );
}
